import { ref } from 'vue';

/**
 * On-screen debug log shared by the radar app: mirrors traces, console errors and global
 * failures onto the screen so they can be read inside the Telegram Mini App (or any phone)
 * without attaching DevTools.
 *
 * Module-level so both the app entry (live-main.ts) and the page component see one log:
 * errors raised before the page mounts are still captured.
 */

export interface DebugLogEntry {
  t: string; // HH:MM:SS.mmm
  msg: string;
  level: 'log' | 'warn' | 'error';
  /** Consecutive repeats of the same message, collapsed into one line (shown as ×N). */
  count: number;
}

export const debugLog = ref<DebugLogEntry[]>([]);

const MAX_ENTRIES = 200;

export function fmtArg(a: unknown): string {
  if (a instanceof Error) {
    // First stack frames carry the file:line that "Script error." hides — keep them short.
    const stack = a.stack ? `\n${a.stack.split('\n').slice(0, 4).join('\n')}` : '';
    return `${a.name}: ${a.message}${stack}`;
  }
  if (typeof a === 'object' && a !== null) {
    try {
      return JSON.stringify(a);
    } catch {
      return String(a);
    }
  }
  return String(a);
}

function pushLog(level: DebugLogEntry['level'], args: unknown[]): void {
  const msg = args.map(fmtArg).filter((s) => s !== '').join(' ');
  const t = new Date().toISOString().slice(11, 23);
  const last = debugLog.value[debugLog.value.length - 1];
  // Collapse repeats: a failing per-chunk handler would otherwise flood all 200 slots
  // with the same line and push everything useful out of the log.
  if (last && last.msg === msg && last.level === level) {
    last.count += 1;
    last.t = t;
    return;
  }
  debugLog.value.push({ t, msg, level, count: 1 });
  if (debugLog.value.length > MAX_ENTRIES) debugLog.value.shift();
}

export function clearDebugLog(): void {
  debugLog.value = [];
}

// Originals captured before the console hooks below, so trace.* never re-enters the panel.
const original = {
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

/**
 * Verbose radar tracing (per-WS-message quadrant dumps etc.). Off by default: those payloads are
 * built with .map()/JSON.stringify on every socket message, so gating them keeps the hot path
 * allocation-free unless a developer opts in. Toggle via localStorage 'pushka:radarTrace'.
 */
const VERBOSE_KEY = 'pushka:radarTrace';
let verboseEnabled = readVerbose();
function readVerbose(): boolean {
  try {
    return localStorage.getItem(VERBOSE_KEY) === '1';
  } catch {
    return false;
  }
}
export function setRadarTraceVerbose(on: boolean): void {
  verboseEnabled = on;
  try {
    localStorage.setItem(VERBOSE_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}
export function isRadarTraceVerbose(): boolean {
  return verboseEnabled;
}

/** Log to the console AND the on-screen panel. Use instead of console.* for radar traces. */
export const trace = {
  log: (...args: unknown[]) => {
    original.log(...args);
    pushLog('log', args);
  },
  warn: (...args: unknown[]) => {
    original.warn(...args);
    pushLog('warn', args);
  },
  error: (...args: unknown[]) => {
    original.error(...args);
    pushLog('error', args);
  },
  /**
   * Verbose trace whose payload is expensive to build. The thunk runs ONLY when verbose tracing
   * is enabled, so callers on hot paths (every socket message) pay nothing when it's off.
   */
  lazy: (build: () => unknown[], level: DebugLogEntry['level'] = 'log') => {
    if (!verboseEnabled) return;
    const args = build();
    original[level](...args);
    pushLog(level, args);
  },
};

let installed = false;

/**
 * Hook global error sources into the on-screen log. Idempotent; never uninstalled —
 * the capture is app-wide, not tied to a component's lifecycle.
 */
export function installGlobalErrorCapture(): void {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (ev: ErrorEvent) => {
    if (ev.error) {
      pushLog('error', [
        '[window.error]',
        ev.error,
        ev.filename ? `@${ev.filename}:${ev.lineno}:${ev.colno}` : '',
      ]);
    } else if (!ev.message || /^Script error\.?$/.test(ev.message)) {
      // Cross-origin scripts (e.g. onnxruntime WASM from the CDN) are masked by the browser
      // to a bare "Script error." — name the cause so the line is actionable on a phone.
      pushLog('error', [
        '[window.error] ошибка в кросс-доменном скрипте — детали скрыты браузером',
        '(вероятно onnxruntime-web с cdn.jsdelivr.net; полный текст см. строкой выше из console.error)',
      ]);
    } else {
      pushLog('error', [
        '[window.error]',
        ev.message,
        ev.filename ? `@${ev.filename}:${ev.lineno}:${ev.colno}` : '',
      ]);
    }
  });

  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    pushLog('error', ['[unhandledrejection]', ev.reason]);
  });

  // Mirror console.warn/error into the panel: libraries (ONNX Runtime, Leaflet, socket.io)
  // report failures there with full detail that window.onerror would mask as "Script error.".
  console.warn = (...args: unknown[]) => {
    original.warn(...args);
    pushLog('warn', args);
  };
  console.error = (...args: unknown[]) => {
    original.error(...args);
    pushLog('error', args);
  };
}
